import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, LogIn, LogOut, ShieldAlert, KeyRound, UserPlus, UserCog, Trash2 } from 'lucide-react';
import { authLogsAPI } from '../api';
import { Card, Table, Pagination, PageHeader, Button, useSort } from '../components/UI';

const EVENT_META = {
  login_success:        { label: 'Login Success',  icon: LogIn,      color: 'var(--accent-green)' },
  login_failed:         { label: 'Login Failed',    icon: ShieldAlert, color: 'var(--accent-red)' },
  logout:                { label: 'Logout',          icon: LogOut,     color: 'var(--text-muted)' },
  token_refresh:        { label: 'Token Refresh',   icon: RefreshCw,  color: 'var(--accent-blue)' },
  api_token_created:    { label: 'API Token Created', icon: KeyRound, color: 'var(--accent-orange)' },
  api_token_revoked:    { label: 'API Token Revoked', icon: KeyRound, color: 'var(--accent-red)' },
  admin_user_created:   { label: 'Admin User Created', icon: UserPlus, color: 'var(--accent-green)' },
  admin_user_updated:   { label: 'Admin User Updated', icon: UserCog,  color: 'var(--accent-blue)' },
  admin_user_deleted:   { label: 'Admin User Deleted', icon: Trash2,   color: 'var(--accent-red)' },
};

function EventBadge({ type }) {
  const meta = EVENT_META[type] || { label: type, icon: ShieldAlert, color: 'var(--text-muted)' };
  const Icon = meta.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 9px',
      borderRadius: 20, background: `${meta.color}1A`, color: meta.color, border: `1px solid ${meta.color}44`, fontWeight: 500,
    }}>
      <Icon size={11} /> {meta.label}
    </span>
  );
}

function MetadataCell({ row }) {
  const m = row.metadata;
  if (!m) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
  const parts = [];
  if (m.target_username) parts.push(`target: ${m.target_username}`);
  if (m.target_role) parts.push(`role: ${m.target_role}`);
  if (m.name) parts.push(`name: ${m.name}`);
  if (m.permission_level) parts.push(`level: ${m.permission_level}`);
  if (m.changed_fields) parts.push(`fields: ${m.changed_fields.join(', ')}`);
  const text = parts.length ? parts.join(' · ') : JSON.stringify(m);
  return (
    <span style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }} title={text}>
      {text}
    </span>
  );
}

const EVENT_TYPES = ['', 'login_success', 'login_failed', 'token_refresh', 'api_token_created', 'api_token_revoked', 'admin_user_created', 'admin_user_updated', 'admin_user_deleted'];

export default function AuthLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [eventType, setEventType] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const { sort, onSortChange, sortParam } = useSort('created_at', 'desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (eventType) params.event_type = eventType;
      if (q) params.q = q;
      if (sortParam) params.sort = sortParam;
      const res = await authLogsAPI.list(params);
      setLogs(res.auth_logs || []);
      setTotal(res.total ?? res.auth_logs?.length ?? 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { /* non-critical */ }
    finally { setLoading(false); }
  }, [page, perPage, eventType, q, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [eventType, q]);

  const columns = [
    { key: 'created_at', label: 'Time', sortable: true, render: v => <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? new Date(v).toLocaleString('id-ID', { hour12: false }) : '—'}</span> },
    { key: 'event_type', label: 'Event', sortable: true, render: v => <EventBadge type={v} /> },
    {
      key: 'admin_user_username', label: 'Admin User', sortable: true, render: (v, row) => (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{v || row.attempted_username || '—'}</div>
          {row.admin_user_role && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{row.admin_user_role}</div>}
        </div>
      )
    },
    { key: 'ip_address', label: 'IP', sortable: true, render: v => <span className="mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v || '—'}</span> },
    {
      key: 'user_agent', label: 'Device', render: v => {
        if (!v) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
        const browser = /Edg\//.test(v) ? 'Edge' : /Chrome\//.test(v) ? 'Chrome' : /Firefox\//.test(v) ? 'Firefox' : /Safari\//.test(v) ? 'Safari' : 'Browser';
        const os = /Windows/.test(v) ? 'Windows' : /Mac OS/.test(v) ? 'macOS' : /Linux/.test(v) ? 'Linux' : /Android/.test(v) ? 'Android' : /iPhone|iPad/.test(v) ? 'iOS' : '';
        return <span style={{ fontSize: 11, color: 'var(--text-secondary)' }} title={v}>{browser}{os ? ` · ${os}` : ''}</span>;
      }
    },
    { key: 'metadata', label: 'Detail', render: (_, row) => <MetadataCell row={row} /> },
  ];

  return (
    <div className="animate-in">
      <PageHeader title="Auth Log" subtitle={`${total} aktivitas autentikasi & audit akun`} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari username…" value={q} onChange={e => setQ(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={eventType} onChange={e => setEventType(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Event</option>
          {EVENT_TYPES.slice(1).map(t => <option key={t} value={t}>{EVENT_META[t]?.label || t}</option>)}
        </select>
        <Button variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Button>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={logs} loading={loading} sort={sort} onSortChange={onSortChange} emptyMessage="Belum ada auth log" />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} />
        </div>
      </Card>
    </div>
  );
}
